import { DOCUMENT } from "@angular/common";
import {
  Inject,
  Injectable,
} from "@angular/core";
import { ActivatedRouteSnapshot, RouterStateSnapshot } from "@angular/router";
import { of } from "rxjs";
import { first, map, switchMap } from "rxjs/operators";
import { GroupsService } from "src/app/groups/groups.service";
import { ExdrLanguageService } from "src/app/languages/services/exdr-language.service";
import { environment } from "src/environments/environment";
import { SubTypeEnum, WebMenuEnum } from "../enums/enums";
import { TabKeyEnum } from "../enums/tab-key-enum";
import { TabInterface, TabOrderInterface } from "../interfaces/tab-interface";
import { Addon } from "../models/addon.model";
import { AuthService, AuthType } from "../services/auth.service";
import { ScriptLoadService } from "../services/scripts-load.service";
import { SharedService } from "../shared.service";
import { UtilsService } from "../utils.service";

@Injectable({ providedIn: 'root' })
export class TabsResolver {
  tabs: TabInterface[] = [];
  addons: Addon[] = [];
  groupLangAddon: Addon;
  webMenu: WebMenuEnum;
  userSubGroups: string[] = [];
  showAllTabs: boolean = false;
  hasFullControl: boolean = false;
  constructor(private authService: AuthService,
    private utilsService: UtilsService,
    private sharedService: SharedService,
    private exdrLangService: ExdrLanguageService,
    @Inject(DOCUMENT) public document: Document,
    private scriptLoadService: ScriptLoadService,
    private groupsService: GroupsService) { }

  resolve(route: ActivatedRouteSnapshot, state: RouterStateSnapshot) {
    return this.initializeTabs();
  }


  initializeTabs() {
    return this.groupsService.hasFullControl(this.authService.idenediGroupId).pipe(switchMap(hasFullControl => {
      this.hasFullControl = hasFullControl;
      this.showAllTabs = hasFullControl && this.authService.showHiddenTabs;
      return this.authService
        .getAddonsAuth(this.authService.idenediGroupId).pipe(
          first(),
          switchMap((addons) => {
            if (this.authService.isLoggedIn()) {
              return this.groupsService
                .fetchUserSubGroups(this.authService.idenediGroupId)
                .pipe(
                  map((subgroups) => {
                    this.userSubGroups = subgroups;
                    return addons;
                  })
                );
            }
            return of(addons)
          }), switchMap(addons => {
            return this.initTabs(addons);
          })

        )
    }))
  }

  replaceTabOrder(addons: Addon[], tabOrderAddon: Addon) {
    addons = this.addons;
    // Find index of tabOrder object in addons array
    const tabOrderIndex = addons.findIndex(addon => addon.Type === "tabOrder");
    if (tabOrderIndex !== -1) {
      // Replace the existing object with the new one
      addons[tabOrderIndex] = tabOrderAddon;
    } else {
    }
    return this.initTabs(addons)

  }

  removeTabsFromCustomTabs(customTabs: any[], homeTabOrderItems: any[]): any[] {
    // Extract and clean up all the keys from homeTabOrderItems
    const homeTabKeys = homeTabOrderItems.map(item => item.key.replace(/^custom tab_?/, ''));
    // Filter customTabs to remove any tab that has a matching customTabId
    const filteredCustomTabs = customTabs.filter(tab => !homeTabKeys.includes((tab.customTabId).replace(/^_/, '')));
    return filteredCustomTabs;
  }


  initTabOrderAddon(tabOrderAddon: Addon) {
    return this.groupsService.hasFullControl(this.authService.idenediGroupId).pipe(switchMap(hasFullControl => {
      this.hasFullControl = hasFullControl;
      this.showAllTabs = hasFullControl && this.authService.showHiddenTabs;
      return this.authService
        .getAddonsAuth(this.authService.idenediGroupId).pipe(
          first(),
          switchMap((addons) => {
            if (this.authService.isLoggedIn()) {
              return this.groupsService
                .fetchUserSubGroups(this.authService.idenediGroupId)
                .pipe(
                  map((subgroups) => {
                    this.userSubGroups = subgroups;
                    return addons;
                  })
                );
            }
            return of(addons)
          }), switchMap(addons => {
            this.replaceTabOrder(addons, tabOrderAddon);
            return this.initTabs(addons);
          })

        )
    }))

  }

  initTabsVersion2(tabOrderAddon: Addon) {
    let utilityTextTabs: TabInterface[] = [];
    let utilityIconTabs: TabInterface[] = [];
    const appApprearanceAddon = this.addons.find((addon) => addon?.Type.toLowerCase() === AuthType.AppAppearance.toLowerCase());
    this.webMenu = appApprearanceAddon?.PublicProperties?.directURLNavigationMenu ?? WebMenuEnum.SIGN_IN_UP;
    const appearanceLogoUrl = appApprearanceAddon?.PublicProperties?.logoUrl;
    const isPrimaryNavbar = appApprearanceAddon?.PublicProperties?.lightNavBar === "1"
    const isAdvanceNav = tabOrderAddon?.PublicProperties?.advancenavmenu === "1";
    const isSideNav = tabOrderAddon?.PublicProperties?.sidenav === "1";
    if (isAdvanceNav) {
      this.document.documentElement.style.setProperty(
        "--header-height",
        "7.375rem"
      );
    } else {
      this.document.documentElement.style.setProperty(
        "--header-height",
        "3.5rem"
      );
    }
    const isFooterActive = tabOrderAddon?.PublicProperties?.footermenu === '1'
    let tabs: TabInterface[] = this.sharedService.getHeaderTabs({
      excluded: [TabKeyEnum.ContactCard, TabKeyEnum.Boards],
    });

    tabs = this.filterAvailableTabs(tabs);
    let customTabs = this.addons?.filter((item) => item?.Type?.toLowerCase() == AuthType.CustomTab.toLowerCase())
      .map((item) => item?.PublicProperties);
    const isEspEnabled = this.addons?.some((a) => a.Type.toLowerCase() === AuthType.ESP.toLowerCase());
    const isSearchEnabled = this.addons?.some((a) => a.Type.toLowerCase() === AuthType.GlobalSearch.toLowerCase());
    this.groupLangAddon = this.addons?.find((addon) => addon?.Type?.toLowerCase() === AuthType.GroupLanguages.toLowerCase());
    let customMenuTabs: TabInterface[] = this.sharedService.convertCustomsTabToTabs(customTabs);
    let tabOrderItems: TabOrderInterface[] = tabOrderAddon?.PublicProperties?.jsonData?.data || [];
    let utilityIconTabOrderItems: TabOrderInterface[] = tabOrderAddon?.PublicProperties?.jsonData?.utilities || [];
    let utilityTextTabOrderItems: TabOrderInterface[] = tabOrderAddon?.PublicProperties?.jsonData?.utilitiesText || [];
    let homeTabOrderItems: TabOrderInterface[] = tabOrderAddon?.PublicProperties?.jsonData?.home || [];
    customTabs = this.removeTabsFromCustomTabs(customTabs, homeTabOrderItems);
    // Remove homeTabOrderItems from custom tabs to avoid them showing up in the header

    utilityIconTabOrderItems = utilityIconTabOrderItems.map((t) => {
      t.utilityType = "icon";
      return t;
    });
    utilityTextTabOrderItems = utilityTextTabOrderItems.map((t) => {
      t.utilityType = "text";
      return t;
    });

    let floatingTabOrderItems: TabOrderInterface[] = tabOrderAddon?.PublicProperties?.jsonData?.floatingTab || [];

    tabOrderItems = tabOrderItems
      .concat(utilityIconTabOrderItems)
      .concat(utilityTextTabOrderItems)
      .concat(floatingTabOrderItems)
      .concat(homeTabOrderItems);
    tabs = this.initTabVisibility(tabOrderItems, tabs);
    customMenuTabs = this.initTabVisibility(tabOrderItems, customMenuTabs);

    tabs.push(
      ...customMenuTabs.map((customTab) => {
        const { subtype, ...props } = customTab;
        return { ...props };
      })
    );
    this.tabs = tabs.map((t) => this.handleTabTranslation(t));
    this.removeRelatedTabs();

    this.configureGroupTabs();
    this.tabs = this.tabs.filter((tab) => (((tab.isvisible) || (this.showAllTabs && tab?.viewOn?.includes('Web'))) && !tab.isLandingPage));
    if (isAdvanceNav) {
      if (this.authService.isLoggedIn()) {
        utilityIconTabs = this.tabs.filter((t) => t.utilityType == "icon");
        // Filter utilityTextTabs based on the original order in utilityTextTabOrderItems
        utilityTextTabs = utilityTextTabOrderItems
          .map((orderItem) => this.tabs.find((tab) => tab.key === orderItem.key))
          .filter((tab): tab is TabInterface => !!tab);
      } else {
        // If user is not logged in, show profile as text tab as always. If web menu is anything other than profile (4).
        utilityTextTabs = this.tabs.filter((t) => {
          return t.utilityType == "text" ||
            (t.key == 'profile' && t.utilityType == 'icon' && t.webMenu != "4");
        });

        // For icon tabs, exclude 'profile' when webMenu is not 4.
        utilityIconTabs = this.tabs.filter((t) =>
          t.utilityType == "icon" && (t.key != 'profile' || t.webMenu == "4")
        );
      }
      this.tabs = this.tabs.filter((tab) => !tab.utilityType || tab.utilityType == 'floatingTab');
    } else {
      utilityTextTabs = [];

      const utilityTabsKeys = this.sharedService
        .getHeaderTabs()
        .filter((t) => t.utilityType == "icon" || t.utilityType == "text")
        .map((t) => t.key);
      // utilityIconTabs = this.tabs.filter(
      //   (tab) =>
      //     utilityTabsKeys.includes(tab.key) && tab.key != TabKeyEnum.Language && tab.key != TabKeyEnum.Search
      // );
      utilityIconTabs = this.tabs.filter(
        (tab) =>
          utilityTabsKeys.includes(tab.key) && tab.key != TabKeyEnum.Language && tab.key != TabKeyEnum.Search
      );
      this.tabs = this.tabs.filter((tab) => !utilityTabsKeys.includes(tab.key));
      utilityIconTabs = utilityIconTabs?.filter(t => t.key != TabKeyEnum.EspAttendance);

    }

    if (this.tabs.some(t => t.key == TabKeyEnum.CustomersService)) {
      const virtualRoomAddon = this.addons?.find(a => a?.Type?.toLowerCase() == AuthType.VirtualRoom.toLowerCase());
      if (virtualRoomAddon) {
        this.sharedService.initializeVirtualRoom(virtualRoomAddon);
      }
    }


    const homeTabKeys = homeTabOrderItems.map(item => item.key.replace(/^custom tab_?/, ''));

    // Separate matching tabs into homeTabs before removing them
    let homeTabs = tabs
      .filter(tab => {
        const cleanTabKey = tab.key.replace(/^custom tab_?/, '');
        return homeTabKeys.includes(cleanTabKey);
      })
      .sort((a, b) => {
        const keyA = a.key.replace(/^custom tab_?/, '');
        const keyB = b.key.replace(/^custom tab_?/, '');
        return homeTabKeys.indexOf(keyA) - homeTabKeys.indexOf(keyB);
      });

    // Remove matching tabs from tabs
    tabs = tabs.filter(tab => {
      const cleanTabKey = tab.key.replace(/^custom tab_?/, '');
      return !homeTabKeys.includes(cleanTabKey); // Keep only non-matching tabs
    });

    // Remove matching tabs from tabs
    this.tabs = this.tabs.filter(tab => {
      const cleanTabKey = tab.key.replace(/^custom tab_?/, '');
      return !homeTabKeys.includes(cleanTabKey); // Keep only non-matching tabs
    });

    this.sharedService.tabs = this.tabs;

    return of({
      tabs: this.tabs.sort((a: any, b: any) => (a?.order) - (b?.order)),
      utilityTextTabs: utilityTextTabs.sort((a: any, b: any) => (a?.order) - (b?.order)),
      utilityIconTabs: utilityIconTabs.sort((a: any, b: any) => (a?.order) - (b?.order)),
      isPrimaryNavbar: isPrimaryNavbar,
      appearanceLogoUrl: appearanceLogoUrl,
      isAdvanceNav,
      isEspEnabled,
      isSearchEnabled,
      isFooterActive,
      isSideNav,
      homeTabs: homeTabs
    })
  }

  initTabs(addons: Addon[]) {
    let utilityTextTabs: TabInterface[] = [];
    let utilityIconTabs: TabInterface[] = [];
    this.addons = addons;
    const appApprearanceAddon = addons.find((addon) => addon?.Type.toLowerCase() === AuthType.AppAppearance.toLowerCase());
    this.webMenu = appApprearanceAddon?.PublicProperties?.directURLNavigationMenu ?? WebMenuEnum.SIGN_IN_UP;
    const appearanceLogoUrl = appApprearanceAddon?.PublicProperties?.logoUrl;
    const isPrimaryNavbar = appApprearanceAddon?.PublicProperties?.lightNavBar === "1"
    const tabOrderAddon = addons?.find((a) => a.Type.toLowerCase() === AuthType.TabOrder.toLowerCase());
    const isAdvanceNav = tabOrderAddon?.PublicProperties?.advancenavmenu === "1";
    const isSideNav = tabOrderAddon?.PublicProperties?.sidenav === "1";
    if (isAdvanceNav) {
      this.document.documentElement.style.setProperty(
        "--header-height",
        "7.375rem"
      );
    } else {
      this.document.documentElement.style.setProperty(
        "--header-height",
        "3.5rem"
      );
    }
    const isFooterActive = tabOrderAddon?.PublicProperties?.footermenu === '1'
    let tabs: TabInterface[] = this.sharedService.getHeaderTabs({
      excluded: [TabKeyEnum.ContactCard, TabKeyEnum.Boards],
    });

    tabs = this.filterAvailableTabs(tabs);
    let customTabs = addons?.filter((item) => item?.Type?.toLowerCase() == AuthType.CustomTab.toLowerCase())
      .map((item) => item?.PublicProperties);
    const isEspEnabled = addons?.some((a) => a.Type.toLowerCase() === AuthType.ESP.toLowerCase());
    const isSearchEnabled = addons?.some((a) => a.Type.toLowerCase() === AuthType.GlobalSearch.toLowerCase());
    this.groupLangAddon = addons?.find((addon) => addon?.Type?.toLowerCase() === AuthType.GroupLanguages.toLowerCase());
    let customMenuTabs: TabInterface[] = this.sharedService.convertCustomsTabToTabs(customTabs);
    let tabOrderItems: TabOrderInterface[] = tabOrderAddon?.PublicProperties?.jsonData?.data || [];
    let utilityIconTabOrderItems: TabOrderInterface[] = tabOrderAddon?.PublicProperties?.jsonData?.utilities || [];
    let utilityTextTabOrderItems: TabOrderInterface[] = tabOrderAddon?.PublicProperties?.jsonData?.utilitiesText || [];
    let homeTabOrderItems: TabOrderInterface[] = tabOrderAddon?.PublicProperties?.jsonData?.home || [];
    customTabs = this.removeTabsFromCustomTabs(customTabs, homeTabOrderItems);
    // Remove homeTabOrderItems from custom tabs to avoid them showing up in the header

    utilityIconTabOrderItems = utilityIconTabOrderItems.map((t) => {
      t.utilityType = "icon";
      return t;
    });
    utilityTextTabOrderItems = utilityTextTabOrderItems.map((t) => {
      t.utilityType = "text";
      return t;
    });

    let floatingTabOrderItems: TabOrderInterface[] = tabOrderAddon?.PublicProperties?.jsonData?.floatingTab || [];

    tabOrderItems = tabOrderItems
      .concat(utilityIconTabOrderItems)
      .concat(utilityTextTabOrderItems)
      .concat(floatingTabOrderItems)
      .concat(homeTabOrderItems);
    tabs = this.initTabVisibility(tabOrderItems, tabs);
    customMenuTabs = this.initTabVisibility(tabOrderItems, customMenuTabs);

    tabs.push(
      ...customMenuTabs.map((customTab) => {
        const { subtype, ...props } = customTab;
        return { ...props };
      })
    );
    this.tabs = tabs.map((t) => this.handleTabTranslation(t));
    this.removeRelatedTabs();

    this.configureGroupTabs();
    this.tabs = this.tabs.filter((tab) => (((tab.isvisible) || (this.showAllTabs && tab?.viewOn?.includes('Web'))) && !tab.isLandingPage));
    if (isAdvanceNav) {
      if (this.authService.isLoggedIn()) {
        utilityIconTabs = this.tabs.filter((t) => t.utilityType == "icon");
        // Filter utilityTextTabs based on the original order in utilityTextTabOrderItems
        utilityTextTabs = utilityTextTabOrderItems
          .map((orderItem) => this.tabs.find((tab) => tab.key === orderItem.key))
          .filter((tab): tab is TabInterface => !!tab);
      } else {
        // If user is not logged in, show profile as text tab as always. If web menu is anything other than profile (4).
        utilityTextTabs = this.tabs.filter((t) => {
          return t.utilityType == "text" ||
            (t.key == 'profile' && t.utilityType == 'icon' && t.webMenu != "4");
        });

        // For icon tabs, exclude 'profile' when webMenu is not 4.
        utilityIconTabs = this.tabs.filter((t) =>
          t.utilityType == "icon" && (t.key != 'profile' || t.webMenu == "4")
        );
      }
      this.tabs = this.tabs.filter((tab) => !tab.utilityType || tab.utilityType == 'floatingTab');
    } else {
      utilityTextTabs = [];

      const utilityTabsKeys = this.sharedService
        .getHeaderTabs()
        .filter((t) => t.utilityType == "icon" || t.utilityType == "text")
        .map((t) => t.key);
      utilityIconTabs = this.tabs.filter(
        (tab) =>
          utilityTabsKeys.includes(tab.key) && tab.key != TabKeyEnum.Language && tab.key != TabKeyEnum.Search
      );
      this.tabs = this.tabs.filter((tab) => !utilityTabsKeys.includes(tab.key));
      utilityIconTabs = utilityIconTabs?.filter(t => t.key != TabKeyEnum.EspAttendance);

    }

    if (this.tabs.some(t => t.key == TabKeyEnum.CustomersService)) {
      const virtualRoomAddon = addons?.find(a => a?.Type?.toLowerCase() == AuthType.VirtualRoom.toLowerCase());
      if (virtualRoomAddon) {
        this.sharedService.initializeVirtualRoom(virtualRoomAddon);
      }
    }


    const homeTabKeys = homeTabOrderItems.map(item => item.key.replace(/^custom tab_?/, ''));

    // Separate matching tabs into homeTabs before removing them
    let homeTabs = tabs
      .filter(tab => {
        const cleanTabKey = tab.key.replace(/^custom tab_?/, '');
        return homeTabKeys.includes(cleanTabKey);
      })
      .sort((a, b) => {
        const keyA = a.key.replace(/^custom tab_?/, '');
        const keyB = b.key.replace(/^custom tab_?/, '');
        return homeTabKeys.indexOf(keyA) - homeTabKeys.indexOf(keyB);
      });

    // Remove matching tabs from tabs
    tabs = tabs.filter(tab => {
      const cleanTabKey = tab.key.replace(/^custom tab_?/, '');
      return !homeTabKeys.includes(cleanTabKey); // Keep only non-matching tabs
    });

    // Remove matching tabs from tabs
    this.tabs = this.tabs.filter(tab => {
      const cleanTabKey = tab.key.replace(/^custom tab_?/, '');
      return !homeTabKeys.includes(cleanTabKey); // Keep only non-matching tabs
    });

    this.sharedService.tabs = this.tabs;

    return of({
      tabs: this.tabs.sort((a: any, b: any) => (a?.order) - (b?.order)),
      utilityTextTabs: utilityTextTabs.sort((a: any, b: any) => (a?.order) - (b?.order)),
      utilityIconTabs: utilityIconTabs.sort((a: any, b: any) => (a?.order) - (b?.order)),
      isPrimaryNavbar: isPrimaryNavbar,
      appearanceLogoUrl: appearanceLogoUrl,
      isAdvanceNav,
      isEspEnabled,
      isSearchEnabled,
      isFooterActive,
      isSideNav,
      homeTabs: homeTabs
    })
  }

  filterAvailableTabs(tabs: TabInterface[]) {
    if (!this.checkTabAvailability(AuthType.ESP)) {
      tabs = tabs.filter(
        (tab) =>
          tab.key !== TabKeyEnum.Requests && tab.key !== TabKeyEnum.EspRecords && tab.key !== TabKeyEnum.EspAttendance && tab.key !== TabKeyEnum.EspSurveys
      );
    } else {
      const espAddon = this.addons.find(
        (a) => a.Type.toLowerCase() == AuthType.ESP.toLowerCase()
      );
      tabs.find((tab) => tab.key === TabKeyEnum.Requests).visibleBy = this._getAddonAccessList(espAddon);
      tabs.find((tab) => tab.key === TabKeyEnum.EspAttendance).visibleBy = this._getAddonAccessList(espAddon);

    }
    if (!this.checkTabAvailability(AuthType.Technadopt)) {
      tabs = tabs.filter((tab) => tab.key !== TabKeyEnum.Library);
    } else {
      const technadoptAddon = this.addons.find(
        (a) => a.Type.toLowerCase() == AuthType.Technadopt.toLowerCase()
      );
      tabs.find((tab) => tab.key === TabKeyEnum.Library).visibleBy =
        this._getAddonAccessList(technadoptAddon);
    }
    if (!this.checkTabAvailability(AuthType.EngagementPro)) {
      tabs = tabs.filter((tab) => tab.key !== TabKeyEnum.EngagementPro);
    }
    if (!this.checkTabAvailability(AuthType.OppPro)) {
      tabs = tabs.filter((tab) => tab.key !== TabKeyEnum.Sellexe);
    }
    if (!this.checkTabAvailability(AuthType.Higher)) {
      tabs = tabs.filter((tab) => tab.key !== TabKeyEnum.Vacancies);
    }
    if (!this.checkTabAvailability(AuthType.PowerBi)) {
      tabs = tabs.filter((tab) => tab.key !== TabKeyEnum.Dashboards);
    }
    if (!this.checkTabAvailability(AuthType.SimpleStrata)) {
      tabs = tabs.filter(
        (tab) =>
          tab.key !== TabKeyEnum.Activities && tab.key !== TabKeyEnum.ScoreCard && tab.key !== TabKeyEnum.ssnotes && tab.key !== TabKeyEnum.SimpleStrataReview
      );
    }
    if (!this.checkTabAvailability(AuthType.Intajy)) {
      tabs = tabs.filter((tab) => tab.key !== TabKeyEnum.Intajy);
    }
    if (!this.checkTabAvailability(AuthType.VirtualRoom)) {
      tabs = tabs.filter((tab) => tab.key !== TabKeyEnum.ConnecteXeDedicationReport);
    }

    if (!this.authService.isLoggedIn()) {
      tabs = tabs.filter((tab) => tab.key !== TabKeyEnum.Chat && tab.key !== TabKeyEnum.EspAttendance);
    }
    return tabs;
  }

  removeRelatedTabs() {
    const relatedTabs = [];
    const vrProductId = !environment.isProduction ? '5f740294-9afb-43de-bfac-939590d57380' : '76930cea-7781-486f-9c19-fb13c5e0e08b';

    if (!this.checkTabAvailability(AuthType.ESP)) {
      relatedTabs.push(...[SubTypeEnum.RequestCatalog, SubTypeEnum.REQUEST, SubTypeEnum.REQUESTS])
    }
    if (!this.checkTabAvailability(AuthType.Technadopt)) {
      relatedTabs.push(SubTypeEnum.TOPIC)
    }
    if (!this.checkTabAvailability(AuthType.PowerBi)) {
      relatedTabs.push(SubTypeEnum.DASHBOARD)
    }
    if (!this.checkTabAvailability(AuthType.Tilda)) {
      relatedTabs.push(SubTypeEnum.TILDA)
    }
    if (!this.checkTabAvailability(AuthType.SimpleStrata)) {
      relatedTabs.push(...[SubTypeEnum.ACTIVITIES, SubTypeEnum.SCORE_CARD])
    }

    if (!relatedTabs?.length) {
      return;
    }
    let filteredTabs = this.tabs.filter(t => relatedTabs.includes(t?.data?.subtype) && !t?.isDefaultTab) ?? [];
    if (!this.checkTabAvailability(AuthType.VirtualRoom)) {
      const connectexeTabs = this.tabs.filter(t => t?.isDefaultTab && t?.data?.productId == vrProductId)
      filteredTabs = filteredTabs?.concat(connectexeTabs)
    }
    if (filteredTabs?.length) {
      this.tabs = this.tabs.filter(t => !filteredTabs.some(ft => ft == t));
    }
  }

  checkTabAvailability(authType: AuthType) {
    return this.addons?.some(add => add.Type?.toLowerCase() === authType.toLowerCase());
  }

  configureGroupTabs() {
    const groupTabs = this.tabs.filter(
      (tab) => tab?.data?.subtype == SubTypeEnum.NAV_GROUP
    );

    groupTabs.map((tab) => {
      tab.data.subTabs = tab?.data?.subTabs
        ?.map((key, index) => {
          const subTab: TabInterface = this.tabs.find(
            (tab) => {
              return tab.key == key && (tab.isvisible || (this.showAllTabs && tab?.viewOn !== ''))
            }
          );
          if (subTab) {
            subTab.isvisible = tab.isvisible;
          }
          this.tabs = this.tabs.filter((tab) => tab.key !== key);
          return subTab;
        })
        .filter((tab) => {
          return !!tab && (tab.isvisible || (this.showAllTabs && tab?.viewOn !== ''))
        });

      return tab;
    }) || [];
    this.tabs = this.tabs.filter(
      (tab) =>
        !(
          tab?.data?.subtype == SubTypeEnum.NAV_GROUP &&
          !tab?.data?.subTabs?.length
        )
    );
  }

  private handleTabTranslation(tab: TabInterface) {
    let langData = this.groupLangAddon?.PublicProperties?.jsonData;
    const locales = Object.keys(langData ?? {});
    if (!locales?.length) {
      return tab;
    }
    let currentLang = langData[this.exdrLangService.currentLanguage?.locale];
    if (currentLang?.settings?.isEnable) {
      const key = tab.key.replace("custom tab", "");
      const tabData = currentLang[key];
      if (tabData?.name) {
        tab.name = tabData?.name;
      }
      if (tab.isvisible && tabData) {
        tab.isvisible = [1, true, "1"].includes(tabData.isEnable) ? 1 : 0;
      }
    }
    return tab;
  }

  public isVisibleForLanguage(tabKey: string, groupLangAddon: Addon) {
    let langData = groupLangAddon?.PublicProperties?.jsonData;
    const locales = Object.keys(langData ?? {});
    if (!locales?.length) {
      return true;
    }
    let currentLang = langData[this.exdrLangService.currentLanguage?.locale];
    if (currentLang?.settings?.isEnable) {
      const key = tabKey.replace("custom tab", "");
      const tabData = currentLang[key];

      if (tabData) {
        return [1, true, "1"].includes(tabData.isEnable) ? 1 : 0;
      }
    }
    return true;
  }

  initTabVisibility(
    tabOrderItems: TabOrderInterface[] = [],
    tabs: TabInterface[]
  ): TabInterface[] {
    return tabs.map((tab) => {
      if (tab.key == TabKeyEnum.Profile) {
        tab.webMenu = this.webMenu;
      }

      const itemOrder = tabOrderItems.find((t) => t.key === tab.key);

      if (itemOrder) {
        tab.order = itemOrder?.order;
        tab.utilityType = itemOrder?.utilityType;
        if (!tab?.key?.startsWith("custom")) {
          tab.viewOn = itemOrder.viewOn;
          if (itemOrder.icon && itemOrder.icon > 1) {
            tab.icon = itemOrder.icon;
            tab.iconPath =
              this.sharedService.getCustomTabIcons(itemOrder.icon - 1)[0]
                ?.path || tab.iconPath;
          }
          tab.name = itemOrder.name || tab.name;
          if (
            tab.key !== TabKeyEnum.Library &&
            tab.key !== TabKeyEnum.Requests
          ) {
            tab.visibleBy = itemOrder.visibleBy;
          }
          tab.hiddenFrom = itemOrder.hiddenFrom;
          if (tab.key == TabKeyEnum.Profile) {
            this.webMenu = itemOrder.webMenu ?? this.webMenu;
            tab.webMenu = this.webMenu;
          }
          if (tab.key == TabKeyEnum.SocialMedia) {
            tab.groupId = itemOrder.groupId ?? this.authService.idenediGroupId;
          }
        }

        if (tab?.data?.subtype == SubTypeEnum.NAV_GROUP) {
          tab.data.subTabs = itemOrder.data?.subTabs ?? [];
        }
      } else {
        if (tab.key == TabKeyEnum.EspRecords || tab.key == TabKeyEnum.EspSurveys) {
          tab.visibleBy = tabs.find(
            (t) => t.key == TabKeyEnum.Requests
          ).visibleBy;
          tab.hiddenFrom = tabs.find(
            (t) => t.key == TabKeyEnum.Requests
          ).hiddenFrom;
        }
        tab.order += 1000;
      }
      if (tab.viewOn == null || tab.viewOn == undefined) {
        tab.isvisible = 1;
      } else {
        tab.isvisible = +tab.viewOn.includes("Web");
      }

      if (tab.isvisible) {
        tab.isvisible = this._isVisibleFor(tab.visibleBy, tab.hiddenFrom) && this.isVisibleForLanguage(tab?.key, this.groupLangAddon) ? 1 : 0;
      }
      if (tab.key == TabKeyEnum.Profile) {
        tab.isvisible = 1;
      }
      // if (tab.key == TabKeyEnum.SocialMedia) {
      //     this.loadSocialContacts(tab);
      // }
      return tab;
    });
  }

  filterHomeTabs(homeTabs: any) {
    homeTabs = homeTabs.filter(tab => {
      // Apply the first visibility check based on viewOn
      if (tab.viewOn == null || tab.viewOn == undefined) {
        tab.isvisible = 1;
      } else {
        tab.isvisible = +tab.viewOn.includes("Web");
      }

      // Apply additional visibility conditions
      if (tab.isvisible) {
        tab.isvisible = this._isVisibleFor(tab.visibleBy, tab.hiddenFrom) &&
          this.isVisibleForLanguage(tab?.key, this.groupLangAddon) ? 1 : 0;
      }

      // Keep only visible tabs in homeTabs
      return tab.isvisible === 1;
    });

    return homeTabs;
  }


  isVisibleBy(visibleBy: { [key: string]: string }[], hiddenFrom: { [key: string]: string }[]) {
    if (!this.authService.isLoggedIn()) {
      return of(this._isVisibleFor(visibleBy, hiddenFrom))
    }
    return this.groupsService
      .fetchUserSubGroups(this.authService.idenediGroupId).pipe(
        map(userSubGroups => {
          this.userSubGroups = userSubGroups
          return this._isVisibleFor(visibleBy, hiddenFrom);
        })
      );
  }

  /**
   * Single place where the two access lists are combined.
   * `hiddenFrom` has priority: an explicit exclusion can never be overridden
   * by `visibleBy`, so a tab hidden from "Non Members" (the `anonymous` entry)
   * stays hidden for logged out visitors.
   */
  private _isVisibleFor(visibleBy: { [key: string]: string }[], hiddenFrom: { [key: string]: string }[]) {
    if (this._isHiddenFrom(hiddenFrom)) {
      return false;
    }
    return this._isVisibleBy(visibleBy);
  }

  private _isVisibleBy(visibleBy: { [key: string]: string }[]) {
    if (!visibleBy) {
      return true;
    }
    if (!visibleBy.length) {
      return false;
    }

    const accessList = visibleBy.map((acl) => Object.keys(acl)).flat();
    if (!this.authService.isLoggedIn()) {
      return accessList.includes("anonymous");
    }
    if (this.hasFullControl) {
      if (this.userSubGroups.some((ug) => accessList.includes(ug)) || accessList?.includes('admins')) {
        return true;
      } else {
        return accessList?.includes('admins');
      }
    }
    if (accessList.length === 1 && accessList.includes("0")) {
      return false;
    }
    return this.userSubGroups.some((ug) => accessList.includes(ug));
  }
  private _isHiddenFrom(hiddenFrom: { [key: string]: string }[]) {
    if (!hiddenFrom) {
      return false;
    }
    if (!hiddenFrom.length) {
      return false;
    }
    const accessList = hiddenFrom.map((acl) => Object.keys(acl)).flat();
    if (!this.authService.isLoggedIn()) {
      return accessList.includes('anonymous');
    }
    if (this.hasFullControl) {
      if (this.userSubGroups.some((ug) => accessList.includes(ug)) || accessList?.includes('admins')) {
        return true;
      } else {
        return accessList?.includes('admins');
      }
    }
    return this.userSubGroups.some((ug) => accessList.includes(ug));
  }
  // only for Request and Library tabs
  private _getAddonAccessList(addon: Addon) {
    let visibleBy = addon?.PublicProperties?.subgroupsAccessList ?
      JSON.parse(JSON.stringify(addon?.PublicProperties?.subgroupsAccessList)) : [];
    if (
      addon?.PublicProperties?.organziationId &&
      this.authService.idenediGroupId
    ) {
      let mainGroup = {};
      mainGroup[this.authService.idenediGroupId] =
        addon?.PublicProperties?.organziationId;
      visibleBy.push(mainGroup);
    }
    return visibleBy;
  }

}
